import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Stack,
  Typography,
  Alert,
  Chip,
  FormControlLabel,
  Checkbox,
  Box,
  CircularProgress,
  RadioGroup,
  Radio,
  OutlinedInput,
  SelectChangeEvent,
  Switch,
  Divider,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  NotificationsActive as NotificationIcon,
  Info as InfoIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { useMutation } from '@tanstack/react-query';

import apiService from '../../services/api';
import {
  ScheduledAnalysis,
  ScheduledAnalysisCreate,
  ScheduleType,
  DateRangeType,
  WatchlistDevice,
  GeofenceOption
} from '../../types/scheduled';
import { AnalysisType } from '../../types';

interface ScheduleDialogProps {
  open: boolean;
  schedule?: ScheduledAnalysis | null;
  onClose: () => void;
  onSuccess: () => void;
}

const WEEKDAYS = [
  { value: 'MON', label: 'Monday' },
  { value: 'TUE', label: 'Tuesday' },
  { value: 'WED', label: 'Wednesday' },
  { value: 'THU', label: 'Thursday' },
  { value: 'FRI', label: 'Friday' },
  { value: 'SAT', label: 'Saturday' },
  { value: 'SUN', label: 'Sunday' }
];

const ANALYSIS_TYPE_INFO = {
  [AnalysisType.FULL]: {
    label: 'Full Analysis',
    description: 'Comprehensive analysis with all features enabled: dwell detection, route patterns, temporal analysis, connectivity, place lookup, and AI insights.',
    duration: '~10-20 minutes'
  },
  [AnalysisType.QUICK]: {
    label: 'Quick Analysis',
    description: 'Fast overview with basic dwell detection and temporal patterns. Ideal for initial assessments and quick insights.',
    duration: '~2-5 minutes'
  },
  [AnalysisType.ROUTE_ONLY]: {
    label: 'Route Only',
    description: 'Focuses on route patterns and movement analysis. Includes dwell detection, route patterns, non-dwell routes, and consistency scoring.',
    duration: '~5-10 minutes'
  },
  [AnalysisType.TEMPORAL_ONLY]: {
    label: 'Temporal Only',
    description: 'Analyzes time-based patterns and activity schedules. Identifies when and how frequently locations are visited.',
    duration: '~3-7 minutes'
  }
};

const ScheduleDialog: React.FC<ScheduleDialogProps> = ({ open, schedule, onClose, onSuccess }) => {
  // Watchlist devices state
  const [watchlistDevices, setWatchlistDevices] = useState<WatchlistDevice[]>([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  
  // Geofences state
  const [geofences, setGeofences] = useState<GeofenceOption[]>([]);
  const [loadingGeofences, setLoadingGeofences] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    device_ids: [] as string[],  // Watchlist device registration IDs
    device_type: 'all',
    analysis_type: AnalysisType.FULL,
    schedule_type: ScheduleType.DAILY,
    schedule_time: new Date(),
    schedule_days: [] as string[],
    date_range_type: DateRangeType.LAST_7_DAYS,
    enable_ai_insights: true,
    processing_mode: 'single' as 'single' | 'individual',
    // Geofence notification settings
    geofence_notification_enabled: false,
    notification_geofence_ids: [] as number[],
    notification_email: '',
    // Report completion notification
    report_notification_enabled: false
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load watchlist devices on mount
  useEffect(() => {
    if (open) {
      setLoadingDevices(true);
      apiService.getWatchlist()
        .then((devices) => {
          setWatchlistDevices(devices);
        })
        .catch((err) => {
          console.error('Failed to load watchlist devices:', err);
        })
        .finally(() => {
          setLoadingDevices(false);
        });
    }
  }, [open]);

  // Load geofences on mount
  useEffect(() => {
    if (open) {
      setLoadingGeofences(true);
      apiService.getGeofences()
        .then((data) => {
          const geofenceOptions: GeofenceOption[] = data.map((g: any) => ({
            id: g.id,
            name: g.name,
            description: g.description,
            active: g.active
          }));
          setGeofences(geofenceOptions);
        })
        .catch((err) => {
          console.error('Failed to load geofences:', err);
        })
        .finally(() => {
          setLoadingGeofences(false);
        });
    }
  }, [open]);

  // Load schedule data when editing
  useEffect(() => {
    if (schedule) {
      const scheduleTime = new Date();
      const [hours, minutes] = schedule.schedule_time.split(':');
      scheduleTime.setHours(parseInt(hours), parseInt(minutes), 0);

      setFormData({
        name: schedule.name,
        description: schedule.description || '',
        device_ids: schedule.device_ids || [],
        device_type: schedule.analysis_config.device_type,
        analysis_type: schedule.analysis_config.analysis_type as AnalysisType,
        schedule_type: schedule.schedule_type,
        schedule_time: scheduleTime,
        schedule_days: schedule.schedule_days?.split(',') || [],
        date_range_type: schedule.date_range_type,
        enable_ai_insights: schedule.analysis_config.parameters?.enable_ai_insights || false,
        processing_mode: schedule.analysis_config.processing_mode || 'single',
        geofence_notification_enabled: schedule.geofence_notification_enabled || false,
        notification_geofence_ids: schedule.notification_geofence_ids || [],
        notification_email: schedule.notification_email || '',
        report_notification_enabled: schedule.report_notification_enabled || false
      });
    } else {
      // Reset for new schedule
      const now = new Date();
      now.setHours(9, 0, 0);  // Default to 9:00 AM
      setFormData({
        name: '',
        description: '',
        device_ids: [],
        device_type: 'all',
        analysis_type: AnalysisType.FULL,
        schedule_type: ScheduleType.DAILY,
        schedule_time: now,
        schedule_days: [],
        date_range_type: DateRangeType.LAST_7_DAYS,
        enable_ai_insights: true,
        processing_mode: 'single',
        geofence_notification_enabled: false,
        notification_geofence_ids: [],
        notification_email: '',
        report_notification_enabled: false
      });
    }
    setErrors({});
  }, [schedule, open]);

  const createMutation = useMutation({
    mutationFn: (data: ScheduledAnalysisCreate) => apiService.createScheduledAnalysis(data),
    onSuccess: () => onSuccess(),
    onError: (error: any) => {
      setErrors({ submit: error.response?.data?.detail || 'Failed to create schedule' });
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => 
      apiService.updateScheduledAnalysis(id, data),
    onSuccess: () => onSuccess(),
    onError: (error: any) => {
      const errorMsg = error.response?.data?.detail || JSON.stringify(error.response?.data) || 'Failed to update schedule';
      setErrors({ submit: errorMsg });
    }
  });

  const handleSubmit = () => {
    // Validation
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (formData.device_ids.length === 0) newErrors.device_ids = 'Select at least one device';
    if (formData.schedule_type === ScheduleType.WEEKLY && formData.schedule_days.length === 0) {
      newErrors.schedule_days = 'Select at least one day';
    }
    if (formData.processing_mode === 'individual' && formData.device_ids.length > 10) {
      newErrors.device_ids = 'Individual processing limited to 10 devices maximum';
    }
    if (formData.geofence_notification_enabled && formData.notification_geofence_ids.length === 0) {
      newErrors.notification_geofence_ids = 'Select at least one geofence for notifications';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Format time as HH:MM:SS
    const hours = formData.schedule_time.getHours().toString().padStart(2, '0');
    const minutes = formData.schedule_time.getMinutes().toString().padStart(2, '0');
    const timeString = `${hours}:${minutes}:00`;

    // Build analysis config
    const analysisConfig = {
      device_type: formData.device_type,
      analysis_type: formData.analysis_type,
      processing_mode: formData.processing_mode,
      parameters: {
        enable_dwell_detection: true,
        enable_dwell_based_routes: true,
        enable_non_dwell_routes: true,
        enable_temporal_analysis: true,
        enable_connectivity_analysis: true,
        enable_route_consistency: true,
        enable_place_lookup: true,
        enable_ai_insights: formData.enable_ai_insights,
        dwell_radius: 50.0,
        stay_point_time_threshold: 300,
        stay_point_distance_threshold: 50.0,
        min_route_length: 5,
        min_segment_distance: 100.0,
        route_similarity_threshold: 50.0,
        max_dwell_locations: 100,
        max_routes: 100
      }
    };

    const scheduleData: any = {
      name: formData.name,
      description: formData.description || undefined,
      analysis_config: analysisConfig,
      schedule_type: formData.schedule_type,
      schedule_time: timeString,
      schedule_days: formData.schedule_type === ScheduleType.WEEKLY 
        ? formData.schedule_days.join(',') 
        : undefined,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,  // User's local timezone
      date_range_type: formData.date_range_type,
      is_active: true,
      // Device-based scheduling
      device_ids: formData.device_ids,
      // Geofence notifications
      geofence_notification_enabled: formData.geofence_notification_enabled,
      notification_geofence_ids: formData.geofence_notification_enabled 
        ? formData.notification_geofence_ids 
        : [],
      notification_email: (formData.geofence_notification_enabled || formData.report_notification_enabled) && formData.notification_email
        ? formData.notification_email 
        : undefined,
      // Report completion notification
      report_notification_enabled: formData.report_notification_enabled
    };

    // Remove undefined values for update to avoid validation errors
    if (schedule) {
      const cleanData = Object.fromEntries(
        Object.entries(scheduleData).filter(([_, v]) => v !== undefined)
      );
      updateMutation.mutate({ id: schedule.id, data: cleanData });
    } else {
      createMutation.mutate(scheduleData);
    }
  };

  const handleDayToggle = (day: string) => {
    setFormData(prev => ({
      ...prev,
      schedule_days: prev.schedule_days.includes(day)
        ? prev.schedule_days.filter(d => d !== day)
        : [...prev.schedule_days, day]
    }));
  };

  const getDeviceDisplayName = (registrationId: string): string => {
    const device = watchlistDevices.find(d => d.registration_id === registrationId);
    if (device?.name) return device.name;
    return registrationId.length > 12 ? `${registrationId.substring(0, 12)}...` : registrationId;
  };

  const getGeofenceName = (id: number): string => {
    const geofence = geofences.find(g => g.id === id);
    return geofence?.name || `Geofence ${id}`;
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {schedule ? 'Edit Scheduled Analysis' : 'Create Scheduled Analysis'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 2 }}>
          {/* Basic Info */}
          <TextField
            label="Schedule Name"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            error={!!errors.name}
            helperText={errors.name || 'e.g., Weekly Engels Air Base, RU Monitoring'}
            fullWidth
            required
          />

          <TextField
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            multiline
            rows={2}
            fullWidth
          />

          {/* Device Selection */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
            Device Selection
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.device_ids}>
                <InputLabel>Saved Devices (Watchlist)</InputLabel>
                <Select
                  multiple
                  value={formData.device_ids}
                  onChange={(e: SelectChangeEvent<string[]>) => {
                    const value = e.target.value;
                    setFormData(prev => ({ 
                      ...prev, 
                      device_ids: typeof value === 'string' ? value.split(',') : value
                    }));
                  }}
                  input={<OutlinedInput label="Saved Devices (Watchlist)" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip 
                          key={value} 
                          label={getDeviceDisplayName(value)} 
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  )}
                  disabled={loadingDevices}
                >
                  {loadingDevices ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Loading devices...
                    </MenuItem>
                  ) : watchlistDevices.length === 0 ? (
                    <MenuItem disabled>
                      <Typography color="text.secondary">
                        No saved devices. Add devices to your watchlist first.
                      </Typography>
                    </MenuItem>
                  ) : (
                    watchlistDevices.map((device) => (
                      <MenuItem key={device.registration_id} value={device.registration_id}>
                        <Box>
                          <Typography variant="body2">
                            {device.name || device.registration_id}
                          </Typography>
                          {device.name && (
                            <Typography variant="caption" color="text.secondary">
                              ID: {device.registration_id.substring(0, 16)}...
                            </Typography>
                          )}
                        </Box>
                      </MenuItem>
                    ))
                  )}
                </Select>
                {errors.device_ids && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                    {errors.device_ids}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl component="fieldset">
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Processing Mode
                </Typography>
                <RadioGroup
                  row
                  value={formData.processing_mode}
                  onChange={(e) => setFormData(prev => ({ ...prev, processing_mode: e.target.value as 'single' | 'individual' }))}
                >
                  <FormControlLabel 
                    value="single" 
                    control={<Radio />} 
                    label={
                      <Box>
                        <Typography variant="body2">Single Combined Report</Typography>
                        <Typography variant="caption" color="text.secondary">
                          All selected devices in one analysis
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel 
                    value="individual" 
                    control={<Radio />} 
                    label={
                      <Box>
                        <Typography variant="body2">Individual Reports (Max 10 devices)</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Separate analysis for each device
                        </Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
          </Grid>

          {/* Analysis Configuration */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
            Analysis Configuration
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Analysis Type</InputLabel>
                <Select
                  value={formData.analysis_type}
                  onChange={(e) => setFormData(prev => ({ ...prev, analysis_type: e.target.value as AnalysisType }))}
                  label="Analysis Type"
                >
                  <MenuItem value={AnalysisType.FULL}>
                    <Tooltip 
                      title={
                        <Box>
                          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                            {ANALYSIS_TYPE_INFO[AnalysisType.FULL].label}
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                            {ANALYSIS_TYPE_INFO[AnalysisType.FULL].description}
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                            Duration: {ANALYSIS_TYPE_INFO[AnalysisType.FULL].duration}
                          </Typography>
                        </Box>
                      }
                      placement="right"
                      arrow
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        Full Analysis
                        <InfoIcon sx={{ ml: 'auto', fontSize: 16, color: 'action.active' }} />
                      </Box>
                    </Tooltip>
                  </MenuItem>
                  <MenuItem value={AnalysisType.QUICK}>
                    <Tooltip 
                      title={
                        <Box>
                          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                            {ANALYSIS_TYPE_INFO[AnalysisType.QUICK].label}
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                            {ANALYSIS_TYPE_INFO[AnalysisType.QUICK].description}
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                            Duration: {ANALYSIS_TYPE_INFO[AnalysisType.QUICK].duration}
                          </Typography>
                        </Box>
                      }
                      placement="right"
                      arrow
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        Quick Analysis
                        <InfoIcon sx={{ ml: 'auto', fontSize: 16, color: 'action.active' }} />
                      </Box>
                    </Tooltip>
                  </MenuItem>
                  <MenuItem value={AnalysisType.ROUTE_ONLY}>
                    <Tooltip 
                      title={
                        <Box>
                          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                            {ANALYSIS_TYPE_INFO[AnalysisType.ROUTE_ONLY].label}
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                            {ANALYSIS_TYPE_INFO[AnalysisType.ROUTE_ONLY].description}
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                            Duration: {ANALYSIS_TYPE_INFO[AnalysisType.ROUTE_ONLY].duration}
                          </Typography>
                        </Box>
                      }
                      placement="right"
                      arrow
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        Route Only
                        <InfoIcon sx={{ ml: 'auto', fontSize: 16, color: 'action.active' }} />
                      </Box>
                    </Tooltip>
                  </MenuItem>
                  <MenuItem value={AnalysisType.TEMPORAL_ONLY}>
                    <Tooltip 
                      title={
                        <Box>
                          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                            {ANALYSIS_TYPE_INFO[AnalysisType.TEMPORAL_ONLY].label}
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                            {ANALYSIS_TYPE_INFO[AnalysisType.TEMPORAL_ONLY].description}
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                            Duration: {ANALYSIS_TYPE_INFO[AnalysisType.TEMPORAL_ONLY].duration}
                          </Typography>
                        </Box>
                      }
                      placement="right"
                      arrow
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        Temporal Only
                        <InfoIcon sx={{ ml: 'auto', fontSize: 16, color: 'action.active' }} />
                      </Box>
                    </Tooltip>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Date Range</InputLabel>
                <Select
                  value={formData.date_range_type}
                  onChange={(e) => setFormData(prev => ({ ...prev, date_range_type: e.target.value as DateRangeType }))}
                  label="Date Range"
                >
                  <MenuItem value={DateRangeType.LAST_7_DAYS}>Last 7 Days</MenuItem>
                  <MenuItem value={DateRangeType.LAST_14_DAYS}>Last 14 Days</MenuItem>
                  <MenuItem value={DateRangeType.LAST_30_DAYS}>Last 30 Days</MenuItem>
                  <MenuItem value={DateRangeType.LAST_90_DAYS}>Last 90 Days</MenuItem>
                  <MenuItem value={DateRangeType.THIS_WEEK}>This Week</MenuItem>
                  <MenuItem value={DateRangeType.LAST_WEEK}>Last Week</MenuItem>
                  <MenuItem value={DateRangeType.THIS_MONTH}>This Month</MenuItem>
                  <MenuItem value={DateRangeType.LAST_MONTH}>Last Month</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          {/* Schedule Configuration */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
            Schedule Configuration
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Frequency</InputLabel>
                <Select
                  value={formData.schedule_type}
                  onChange={(e) => setFormData(prev => ({ ...prev, schedule_type: e.target.value as ScheduleType }))}
                  label="Frequency"
                >
                  <MenuItem value={ScheduleType.ONCE}>Once</MenuItem>
                  <MenuItem value={ScheduleType.DAILY}>Daily</MenuItem>
                  <MenuItem value={ScheduleType.WEEKLY}>Weekly</MenuItem>
                  <MenuItem value={ScheduleType.MONTHLY}>Monthly</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Time (Local)"
                type="time"
                value={formData.schedule_time.toTimeString().substring(0, 5)}
                onChange={(e) => {
                  const [hours, minutes] = e.target.value.split(':');
                  const newTime = new Date();
                  newTime.setHours(parseInt(hours), parseInt(minutes), 0);
                  setFormData(prev => ({ ...prev, schedule_time: newTime }));
                }}
                onClick={(e) => {
                  // Make the entire field clickable to open the time picker
                  const input = e.currentTarget.querySelector('input');
                  if (input) {
                    input.showPicker?.();
                  }
                }}
                fullWidth
                helperText="Schedule will run at this time in your local timezone"
                InputLabelProps={{ shrink: true }}
                sx={{
                  cursor: 'pointer',
                  '& input[type="time"]::-webkit-calendar-picker-indicator': {
                    filter: 'invert(1)',
                    cursor: 'pointer',
                    opacity: 0.7,
                    '&:hover': {
                      opacity: 1
                    }
                  }
                }}
              />
            </Grid>

            {/* Weekly Days Selection */}
            {formData.schedule_type === ScheduleType.WEEKLY && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Select Days
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {WEEKDAYS.map(({ value, label }) => (
                    <Chip
                      key={value}
                      label={label}
                      onClick={() => handleDayToggle(value)}
                      color={formData.schedule_days.includes(value) ? 'primary' : 'default'}
                      variant={formData.schedule_days.includes(value) ? 'filled' : 'outlined'}
                    />
                  ))}
                </Box>
                {errors.schedule_days && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {errors.schedule_days}
                  </Typography>
                )}
              </Grid>
            )}
          </Grid>

          {/* Geofence Notifications Section */}
          <Divider sx={{ my: 2 }} />
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <NotificationIcon color={formData.geofence_notification_enabled ? 'primary' : 'disabled'} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Geofence Notifications
            </Typography>
            <Tooltip title="Get notified when a device is detected inside selected geofences during the analysis period">
              <IconButton size="small">
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={formData.geofence_notification_enabled}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  geofence_notification_enabled: e.target.checked 
                }))}
                color="primary"
              />
            }
            label="Enable geofence presence notifications"
          />

          {formData.geofence_notification_enabled && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth error={!!errors.notification_geofence_ids}>
                  <InputLabel>Geofences to Monitor</InputLabel>
                  <Select
                    multiple
                    value={formData.notification_geofence_ids}
                    onChange={(e: SelectChangeEvent<number[]>) => {
                      const value = e.target.value;
                      setFormData(prev => ({ 
                        ...prev, 
                        notification_geofence_ids: typeof value === 'string' 
                          ? value.split(',').map(Number) 
                          : value
                      }));
                    }}
                    input={<OutlinedInput label="Geofences to Monitor" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((id) => (
                          <Chip 
                            key={id} 
                            label={getGeofenceName(id)} 
                            size="small"
                            color="secondary"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    )}
                    disabled={loadingGeofences}
                  >
                    {loadingGeofences ? (
                      <MenuItem disabled>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        Loading geofences...
                      </MenuItem>
                    ) : geofences.length === 0 ? (
                      <MenuItem disabled>
                        <Typography color="text.secondary">
                          No geofences available. Create geofences first.
                        </Typography>
                      </MenuItem>
                    ) : (
                      geofences.filter(g => g.active).map((geofence) => (
                        <MenuItem key={geofence.id} value={geofence.id}>
                          {geofence.name}
                          {geofence.description && (
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                              - {geofence.description}
                            </Typography>
                          )}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {errors.notification_geofence_ids && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                      {errors.notification_geofence_ids}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Notification Email (optional)"
                  type="email"
                  value={formData.notification_email}
                  onChange={(e) => setFormData(prev => ({ ...prev, notification_email: e.target.value }))}
                  fullWidth
                  helperText="Email address to receive geofence alerts"
                />
              </Grid>

              <Grid item xs={12}>
                <Alert severity="info" sx={{ mt: 1 }}>
                  <Typography variant="caption">
                    When enabled, you'll be notified if any selected device was present in the monitored geofences 
                    during the analysis time period. Analysis is processed by Azure Functions for better performance.
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
          )}

          {/* Report Completion Notifications Section */}
          <Divider sx={{ my: 2 }} />
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmailIcon color={formData.report_notification_enabled ? 'primary' : 'disabled'} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Report Completion Notifications
            </Typography>
            <Tooltip title="Receive an email notification when the scheduled analysis completes">
              <IconButton size="small">
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={formData.report_notification_enabled}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  report_notification_enabled: e.target.checked 
                }))}
                color="primary"
              />
            }
            label="Email me when analysis completes"
          />

          {formData.report_notification_enabled && !formData.geofence_notification_enabled && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Notification Email"
                  type="email"
                  value={formData.notification_email}
                  onChange={(e) => setFormData(prev => ({ ...prev, notification_email: e.target.value }))}
                  fullWidth
                  helperText="Email address to receive report completion notifications"
                />
              </Grid>
            </Grid>
          )}

          {/* Options */}
          <Divider sx={{ my: 1 }} />

          <FormControlLabel
            control={
              <Checkbox
                checked={formData.enable_ai_insights}
                onChange={(e) => setFormData(prev => ({ ...prev, enable_ai_insights: e.target.checked }))}
              />
            }
            label="Enable AI Insights"
          />

          {/* Error Display */}
          {errors.submit && (
            <Alert severity="error">
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {errors.submit}
              </Typography>
            </Alert>
          )}

          {/* Summary Preview */}
          <Alert severity="info">
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              Schedule Summary:
            </Typography>
            <Typography variant="body2">
              <strong>Frequency:</strong> {formData.schedule_type === ScheduleType.WEEKLY && formData.schedule_days.length > 0 
                ? `Every ${formData.schedule_days.join(', ')}`
                : formData.schedule_type === ScheduleType.DAILY
                ? 'Every day'
                : formData.schedule_type === ScheduleType.MONTHLY
                ? 'Every month'
                : 'Once'
              } at {formData.schedule_time.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })} UTC
              <br />
              <strong>Devices:</strong> {formData.device_ids.length > 0 
                ? formData.device_ids.map(id => getDeviceDisplayName(id)).join(', ')
                : 'None selected'}
              <br />
              <strong>Date Range:</strong> {formData.date_range_type.replace(/_/g, ' ')}
              <br />
              <strong>Processing:</strong> {formData.processing_mode === 'individual' 
                ? `${formData.device_ids.length} separate reports` 
                : '1 combined report'}
              {formData.geofence_notification_enabled && (
                <>
                  <br />
                  <strong>Geofence Alerts:</strong> {formData.notification_geofence_ids.length} geofence(s) monitored
                </>
              )}
              {formData.report_notification_enabled && (
                <>
                  <br />
                  <strong>Report Notifications:</strong> Email on completion
                </>
              )}
            </Typography>
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isPending}
        >
          {isPending ? <CircularProgress size={20} /> : (schedule ? 'Update' : 'Create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleDialog;

